import { parseElementName } from '@adflow/types';

const SENSITIVE_KEYS = new Set([
  'importSourceJSON',
  'credentials',
  'userId',
  'token',
  'password',
  'authorization',
  'cookie',
]);

const CONTENT_TOKEN_PATTERN_SOURCE = '\\{(?:[^:]+:)?([A-Z_][A-Z0-9_]*)\\}';

const TEXT_ALIGN = new Set(['left', 'center', 'right']);
const TEXT_CASE = new Set(['none', 'uppercase', 'lowercase', 'capitalize']);

export type TemplateBuilderReadSource = {
  sourceId: string;
  name: string;
};

export type TemplateBuilderReadCustomAsset = {
  folder: string;
  fileTemplate: string;
};

export type TemplateBuilderReadNameConfig = {
  isEditable: boolean;
  customAsset: TemplateBuilderReadCustomAsset | null;
};

export type TemplateBuilderReadDynamicFont =
  | { enabled: false }
  | {
      enabled: true;
      maxFontSize: number;
      minFontSize: number;
      lineHeight: number;
      multiLine: boolean;
    };

export type TemplateBuilderReadStyle = {
  textAlign: string | null;
  fontSize: number | null;
  lineHeight: number | null;
  fontFamily: string | null;
  fontVariant: string | null;
  textCase: string | null;
};

export type TemplateBuilderReadElement = {
  id: string;
  type: string;
  name: string;
  position: { x: number; y: number };
  sources: TemplateBuilderReadSource[];
  nameConfig: TemplateBuilderReadNameConfig | null;
  contentTokens: string[];
  contentTemplate: string | null;
  dynamicFont: TemplateBuilderReadDynamicFont | null;
  style: TemplateBuilderReadStyle | null;
};

export type TemplateBuilderReadDataSource = {
  id: string;
  type: string;
};

export type TemplateBuilderReadData = {
  templateId: string;
  name: string;
  selectedElementId: string | null;
  elementCount: number;
  dataSources: TemplateBuilderReadDataSource[];
  elements: TemplateBuilderReadElement[];
};

type EditorElementLike = {
  id?: unknown;
  type?: unknown;
  name?: unknown;
  position?: { x?: unknown; y?: unknown };
  sources?: unknown;
  data?: unknown;
};

type EditorStateLike = {
  name?: unknown;
  selectedElementId?: unknown;
  elements?: unknown;
  dataSources?: unknown;
};

function asNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function asFiniteNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function looksLikeUrl(value: string): boolean {
  return /^https?:\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:');
}

function projectSources(value: unknown): TemplateBuilderReadSource[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    const source = entry as { sourceId?: unknown; name?: unknown };
    if (typeof source.sourceId !== 'string' || typeof source.name !== 'string') {
      return [];
    }
    return [{ sourceId: source.sourceId, name: source.name }];
  });
}

function projectNameConfig(rawName: string): TemplateBuilderReadNameConfig | null {
  const parsed = parseElementName(rawName);
  if (!parsed.config) {
    return null;
  }

  const customAsset = parsed.config.customAssetConfig
    ? {
        folder: parsed.config.customAssetConfig.folder,
        fileTemplate: parsed.config.customAssetConfig.fileTemplate,
      }
    : null;

  return {
    isEditable: parsed.config.isEditable,
    customAsset,
  };
}

function extractContentTokens(
  content: string | null,
  sources: TemplateBuilderReadSource[],
): string[] {
  const tokens = new Set<string>();
  for (const source of sources) {
    tokens.add(source.name);
  }
  if (content) {
    const tokenPattern = new RegExp(CONTENT_TOKEN_PATTERN_SOURCE, 'gi');
    for (const match of content.matchAll(tokenPattern)) {
      if (match[1]) {
        tokens.add(match[1]);
      }
    }
  }
  return [...tokens];
}

function projectContentTemplate(type: string, content: unknown): string | null {
  if (typeof content !== 'string' || content.length === 0) {
    return null;
  }
  if (looksLikeUrl(content)) {
    return null;
  }
  if (type === 'TEXT') {
    return content;
  }
  if (content.includes('{') && content.includes('}')) {
    return content;
  }
  return null;
}

function projectDynamicFont(data: unknown): TemplateBuilderReadDynamicFont | null {
  if (!data || typeof data !== 'object' || !('dynamicFont' in data)) {
    return null;
  }
  const dynamicFont = (data as { dynamicFont?: unknown }).dynamicFont;
  if (!dynamicFont || typeof dynamicFont !== 'object') {
    return null;
  }

  const enabled = (dynamicFont as { enabled?: unknown }).enabled;
  if (enabled === false) {
    return { enabled: false };
  }
  if (enabled !== true) {
    return null;
  }

  const font = dynamicFont as {
    maxFontSize?: unknown;
    minFontSize?: unknown;
    lineHeight?: unknown;
    multiLine?: unknown;
  };

  return {
    enabled: true,
    maxFontSize: asNumber(font.maxFontSize),
    minFontSize: asNumber(font.minFontSize),
    lineHeight: asNumber(font.lineHeight),
    multiLine: font.multiLine === true,
  };
}

function projectStyle(type: string, data: unknown): TemplateBuilderReadStyle | null {
  if (type !== 'TEXT' || !data || typeof data !== 'object' || !('style' in data)) {
    return null;
  }
  const style = (data as { style?: unknown }).style;
  if (!style || typeof style !== 'object') {
    return null;
  }

  const record = style as {
    textAlign?: unknown;
    fontSize?: unknown;
    lineHeight?: unknown;
    fontFamily?: unknown;
    fontVariant?: unknown;
    textCase?: unknown;
  };

  const textAlign =
    typeof record.textAlign === 'string' && TEXT_ALIGN.has(record.textAlign)
      ? record.textAlign
      : null;
  const textCase =
    typeof record.textCase === 'string' && TEXT_CASE.has(record.textCase) ? record.textCase : null;

  return {
    textAlign,
    fontSize: asFiniteNumberOrNull(record.fontSize),
    lineHeight: asFiniteNumberOrNull(record.lineHeight),
    fontFamily: asStringOrNull(record.fontFamily),
    fontVariant: asStringOrNull(record.fontVariant),
    textCase,
  };
}

function projectElement(element: EditorElementLike): TemplateBuilderReadElement | null {
  if (typeof element.id !== 'string' || typeof element.type !== 'string') {
    return null;
  }

  const name = typeof element.name === 'string' ? element.name : element.id;
  const sources = projectSources(element.sources);
  const data = element.data && typeof element.data === 'object' ? element.data : null;
  const rawContent =
    data && 'content' in data ? (data as { content?: unknown }).content : undefined;
  const contentTemplate = projectContentTemplate(element.type, rawContent);

  return {
    id: element.id,
    type: element.type,
    name,
    position: {
      x: asNumber(element.position?.x),
      y: asNumber(element.position?.y),
    },
    sources,
    nameConfig: projectNameConfig(name),
    contentTokens: extractContentTokens(
      typeof rawContent === 'string' ? rawContent : contentTemplate,
      sources,
    ),
    contentTemplate,
    dynamicFont: element.type === 'TEXT' ? projectDynamicFont(data) : null,
    style: projectStyle(element.type, data),
  };
}

function projectDataSources(value: unknown): TemplateBuilderReadDataSource[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') {
      return [];
    }
    const source = entry as { id?: unknown; type?: unknown };
    if (typeof source.id !== 'string' || typeof source.type !== 'string') {
      return [];
    }
    return [{ id: source.id, type: source.type }];
  });
}

export function sanitizeEditorSummary(
  state: EditorStateLike | null | undefined,
  templateId: string,
): TemplateBuilderReadData | null {
  if (!state || !Array.isArray(state.elements)) {
    return null;
  }

  const elements = (state.elements as EditorElementLike[])
    .map(projectElement)
    .filter((element): element is TemplateBuilderReadElement => element !== null);

  return {
    templateId,
    name: typeof state.name === 'string' ? state.name : '',
    selectedElementId: typeof state.selectedElementId === 'string' ? state.selectedElementId : null,
    elementCount: elements.length,
    dataSources: projectDataSources(state.dataSources),
    elements,
  };
}

export function sanitizeLogValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sanitizeLogValue);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, nested]) => {
        if (SENSITIVE_KEYS.has(key)) {
          return [key, '[redacted]'];
        }
        return [key, sanitizeLogValue(nested)];
      }),
    );
  }

  return value;
}
